import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, pct, thaiDateTime } from '../lib/format'

/**
 * Fraud-ring clustering (part 95).
 *
 * The server owns the graph algorithm: it folds shared accounts, devices and addresses into a
 * weighted entity graph, partitions it with deterministic label propagation and stores every
 * community that clears the minimum-size and containment thresholds. This page never clusters
 * anything itself — it lists the stored rings and shows the stored edges, so the numbers in a
 * screenshot are the numbers the build wrote.
 */

type Band = 'low' | 'elevated' | 'high' | 'critical'

interface Member {
  id: string
  entity_id: string
  entity_kind: string
  entity_kind_label: string
  code: string
  label: string
  degree: number
  weighted_degree: number
  signal_kinds: string[]
  position: number
}

interface Signal {
  kind: string
  label: string
  key: string
  weight: number
  observations: number
  detail: string
}

interface Edge {
  a: string
  b: string
  a_name: string
  b_name: string
  a_code: string
  b_code: string
  weight: number
  signals: Signal[]
}

interface Evidence {
  kind: string
  label: string
  weight: number
  edges: number
  observations: number
}

interface RingView {
  id: string
  cluster_key: string
  score: number
  band: Band
  band_label: string
  member_count: number
  edge_count: number
  density: number
  average_weight: number
  internal_weight: number
  external_weight: number
  containment: number
  modularity: number
  top_signal: string
  signal_kinds: string[]
  explanation: string
  members: Member[]
  computed_at: string
}

interface RingDetail extends RingView {
  edges: Edge[]
  evidence: Evidence[]
}

interface BuildResponse {
  computed_at: string
  entities: number
  edges: number
  rings: number
  members: number
  high_risk: number
  top_score: number
  modularity: number
  iterations: number
  min_ring_size: number
  max_iterations: number
}

const bandTone: Record<Band, string> = {
  low: 'sev-low',
  elevated: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function memberSummary(members: Member[]): string {
  const names = members.slice(0, 3).map((member) => member.label)
  const extra = members.length > 3 ? ` และอีก ${members.length - 3} ราย` : ''
  return `${names.join(', ')}${extra}`
}

export function Clustering() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const rings = useQuery({
    queryKey: ['clustering-rings'],
    queryFn: () => list<RingView>('/v1/clustering/rings?limit=200'),
    staleTime: 30_000,
  })

  const rows = rings.data ?? []
  const activeId = selectedId ?? rows[0]?.id ?? null

  const detail = useQuery({
    queryKey: ['clustering-ring', activeId],
    queryFn: () => api<RingDetail>(`/v1/clustering/rings/${activeId}`),
    enabled: Boolean(activeId),
    staleTime: 60_000,
  })

  const build = useMutation({
    mutationFn: () => apiWrite<BuildResponse>('/v1/clustering/build', 'POST'),
    onSuccess: (result) => {
      setNotice(
        `วิเคราะห์ ${number(result.entities)} รายการหลัก · พบเส้นเชื่อม ${number(result.edges)} เส้น ` +
        `และกลุ่มต้องสงสัย ${number(result.rings)} กลุ่ม (คะแนนสูงสุด ${result.top_score}/100 · ` +
        `modularity ${result.modularity.toFixed(3)})`,
      )
      setSelectedId(null)
      void qc.invalidateQueries({ queryKey: ['clustering-rings'] })
      void qc.invalidateQueries({ queryKey: ['clustering-ring'] })
    },
  })

  const totalMembers = rows.reduce((sum, row) => sum + row.member_count, 0)
  const highRisk = rows.filter((row) => row.score >= 60).length
  const maxScore = rows.reduce((max, row) => Math.max(max, row.score), 0)
  const active = detail.data

  return (
    <>
      <PageHead
        title="การจัดกลุ่มเครือข่ายทุจริต"
        sub="ตรวจหากลุ่มที่เชื่อมโยงกันผ่านบัญชี อุปกรณ์ และที่อยู่ร่วม · จัดกลุ่มด้วย label propagation แบบกำหนดผลได้และอธิบายด้วยน้ำหนักหลักฐาน"
        actions={
          <button
            className="btn btn-primary btn-sm"
            disabled={build.isPending}
            onClick={() => { setNotice(null); build.mutate() }}
          >
            {build.isPending ? 'กำลังวิเคราะห์…' : 'วิเคราะห์ใหม่'}
          </button>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4, marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {build.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(build.error, 'วิเคราะห์กราฟไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="กลุ่มที่ตรวจพบ" value={number(rows.length)} unit="กลุ่ม" foot="เฉพาะกลุ่มที่ผ่านเกณฑ์ขนาดและสัดส่วนหลักฐานภายใน" />
        <Stat label="สมาชิกในกลุ่ม" value={number(totalMembers)} unit="ราย" foot="นับจากสมาชิกของทุกกลุ่ม" />
        <Stat label="คะแนนสูงสุด" value={maxScore} unit="/ 100" foot="ถ่วงน้ำหนักจากความหนาแน่นและหลักฐานภายใน" />
        <Stat
          label="กลุ่มเสี่ยงสูง"
          value={number(highRisk)}
          unit="กลุ่ม"
          foot={build.data ? `โมดูลาริตีรวม ${build.data.modularity.toFixed(3)} · วนซ้ำ ${build.data.iterations} รอบ` : 'กด “วิเคราะห์ใหม่” เพื่อสร้างผลล่าสุด'}
        />
      </div>

      {rings.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={200} /><Skel h={160} /></div>
      ) : rings.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(rings.error, 'โหลดรายการกลุ่มไม่สำเร็จ')}</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่พบกลุ่มเครือข่ายทุจริต"
            body="กด “วิเคราะห์ใหม่” เพื่อสร้างกราฟจากบัญชีเงินฝาก อุปกรณ์ และที่อยู่ตามทะเบียน แล้วจัดกลุ่มรายการที่เชื่อมโยงกันตั้งแต่สามรายขึ้นไป"
          />
        </div>
      ) : (
        <div className="grid-main" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">กลุ่มที่ตรวจพบ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เรียงตามคะแนนความเสี่ยง
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>กลุ่ม</th>
                    <th style={{ textAlign: 'right' }}>ความเสี่ยง</th>
                    <th style={{ textAlign: 'right' }}>สมาชิก</th>
                    <th style={{ textAlign: 'right' }}>ความหนาแน่น</th>
                    <th>หลักฐานหลัก</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      style={{
                        cursor: 'pointer',
                        background: row.id === activeId ? 'rgba(200, 169, 106, 0.06)' : undefined,
                      }}
                    >
                      <td>
                        <div>{memberSummary(row.members)}</div>
                        <div className="dim" style={{ fontSize: 11 }}>{row.cluster_key}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={`sev ${bandTone[row.band]}`}>
                          <i className="sev-dot" />
                          {row.score}
                        </span>
                        <div className="dim" style={{ fontSize: 10.5 }}>{row.band_label}</div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.member_count}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{pct(row.density)}</td>
                      <td>{row.top_signal || 'ไม่ระบุ'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
              เกณฑ์ขั้นต่ำ: สมาชิกอย่างน้อย 3 ราย · ต้องเชื่อมต่อกันภายในกลุ่ม · น้ำหนักเส้นเชื่อมภายในอย่างน้อย 50%
              ของน้ำหนักที่เกี่ยวข้องทั้งหมด · หลักฐานแต่ละประเภทคือบัญชีร่วม 45, อุปกรณ์ร่วม 35 และที่อยู่ร่วม 20 คะแนน
            </div>
          </div>

          <div className="card card-pad">
            {detail.isLoading ? (
              <Skel h={240} />
            ) : detail.isError ? (
              <div className="form-error">{errorText(detail.error, 'โหลดคำอธิบายกลุ่มไม่สำเร็จ')}</div>
            ) : !active ? (
              <Empty title="ยังไม่ได้เลือกกลุ่ม" body="เลือกกลุ่มจากตารางเพื่อดูสมาชิกและหลักฐานรายเส้น" />
            ) : (
              <>
                <div className="card-label">คำอธิบายกลุ่ม</div>
                <div className="row" style={{ alignItems: 'baseline', marginTop: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 300 }} className="tnum">{active.score}</span>
                  <span className="dim" style={{ fontSize: 12 }}>/ 100</span>
                  <span className={`sev ${bandTone[active.band]}`} style={{ marginLeft: 8 }}>
                    <i className="sev-dot" />
                    {active.band_label}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 8 }}>{active.explanation}</div>
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  {active.member_count} ราย · {active.edge_count} เส้น · ความหนาแน่น {pct(active.density)} ·
                  น้ำหนักเฉลี่ย {active.average_weight.toFixed(1)} · modularity {active.modularity.toFixed(3)}
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
                  น้ำหนักภายใน {active.internal_weight.toFixed(0)} · ภายนอก {active.external_weight.toFixed(0)} ·
                  สัดส่วนภายใน {pct(active.containment)}
                </div>

                <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  {active.signal_kinds.map((kind) => (
                    <span key={kind} className="chip gold">
                      {active.evidence.find((item) => item.kind === kind)?.label ?? kind}
                    </span>
                  ))}
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="card-label">สมาชิกในกลุ่ม</div>
                  <div className="stack" style={{ gap: 8, marginTop: 10 }}>
                    {active.members.map((member) => (
                      <div key={member.id} className="row" style={{ alignItems: 'baseline' }}>
                        <div>
                          <div style={{ fontSize: 12.5 }}>{member.label}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            {member.entity_kind_label}{member.code ? ` · ${member.code}` : ''} ·
                            ระดับการเชื่อมโยง {member.degree}
                          </div>
                        </div>
                        <span className="dim tnum" style={{ marginLeft: 'auto', fontSize: 10.5 }}>
                          {member.weighted_degree}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="card-label">เส้นเชื่อมและน้ำหนักหลักฐาน</div>
                  <div className="stack" style={{ gap: 12, marginTop: 10 }}>
                    {active.edges.length === 0 ? (
                      <div className="dim" style={{ fontSize: 12 }}>ไม่พบเส้นเชื่อมภายในกลุ่มนี้</div>
                    ) : (
                      active.edges.map((edge) => (
                        <div key={`${edge.a}:${edge.b}`} style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                          <div className="row" style={{ alignItems: 'baseline' }}>
                            <span style={{ fontSize: 12.5 }}>
                              {edge.a_name}
                              <span className="dim"> และ </span>
                              {edge.b_name}
                            </span>
                            <span className="tnum" style={{ marginLeft: 'auto', fontSize: 12 }}>
                              น้ำหนัก {edge.weight}
                            </span>
                          </div>
                          <div className="bar" style={{ marginTop: 6, width: '100%' }}>
                            <i
                              style={{
                                width: `${Math.min(100, edge.weight)}%`,
                                background: edge.weight >= 80
                                  ? 'var(--color-crimson)'
                                  : edge.weight >= 50
                                    ? 'var(--color-amber)'
                                    : 'var(--color-gold)',
                              }}
                            />
                          </div>
                          <div className="stack" style={{ gap: 4, marginTop: 8 }}>
                            {edge.signals.map((signal) => (
                              <div key={`${signal.kind}:${signal.key}`} className="dim" style={{ fontSize: 11 }}>
                                <span style={{ color: 'var(--color-ivory)' }}>{signal.label}</span>
                                {' · '}
                                {signal.detail}
                                {signal.observations > 1 ? ` · พบ ${number(signal.observations)} ครั้ง` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="dim" style={{ fontSize: 10.5, marginTop: 14 }}>
                  ประเมินเมื่อ {thaiDateTime(active.computed_at)} · รหัสกลุ่ม {active.cluster_key}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
